// Section 4 phase 7 and Section 14: migration (stream E2). Arrivals come as events (the events
// phase and the gate); this phase carries out departures and tracks emigration pressure:
//   1. planned departures due this season leave: exile (Section 13, set by the justice phase in
//      `plannedDeparture` with reason `exile`) takes the exiled person and those who follow; an
//      elder leaving to join kin (Section 19) goes alone; guests turned away leave together;
//      anyone else leaving takes their whole household (Section 14);
//   2. each adult's emigration streak is updated and, after 3 seasons over the threshold, a
//      departure is planned for the next Spring or Summer.
import { peopleSorted } from '../../core/queries';
import type { EmigrationReason, HouseholdId, PersonId, Phase } from '../../core/types';
import { departHousehold, departPeople, exile } from '../shared/depart';
import { updateEmigration } from './pressure';

export const migrationPhase: Phase = {
  id: 'migration',
  reads: ['people', 'households', 'policies', 'currentReport'],
  writes: [
    'people',
    'households',
    'formerPeople',
    'counters',
    'currentReport',
    'stocks',
    'plantedPlots',
    'toolOverrides',
    'triageOverrides',
    'rationOverrides',
    'myHousehold',
  ],
  projection: 'skip',
  run(ctx) {
    const turn = ctx.season.turn;
    const together = new Map<string, { reason: EmigrationReason; ids: PersonId[] }>();
    const households = new Map<HouseholdId, EmigrationReason>();
    const present = ctx.draft.people;
    // Plans and households are read from the committed person (nothing earlier in this phase
    // changes them); the draft only says who has already left (Section 2 budget).
    for (const p of peopleSorted(ctx.state)) {
      const id = p.id;
      const plan = p.plannedDeparture;
      if (plan === null || plan.atTurn > turn || !(id in present)) continue;
      switch (plan.reason) {
        case 'exile':
          exile(ctx, id);
          break;
        case 'kin':
          departPeople(ctx, [id], plan.reason);
          break;
        case 'turned-away': {
          const key = `${p.householdId}:${plan.reason}`;
          const group = together.get(key) ?? { reason: plan.reason, ids: [] };
          group.ids.push(id);
          together.set(key, group);
          break;
        }
        default:
          if (!households.has(p.householdId)) households.set(p.householdId, plan.reason);
      }
    }
    for (const key of [...together.keys()].sort()) {
      const group = together.get(key);
      if (group !== undefined) departPeople(ctx, group.ids, group.reason);
    }
    for (const householdId of [...households.keys()].sort()) {
      const reason = households.get(householdId);
      if (reason !== undefined) departHousehold(ctx, householdId, reason);
    }
    updateEmigration(ctx);
    return 'done';
  },
};
